

import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { namespace, Getter, Action } from "vuex-class"
import DataForm from "../DataForm.vue"
import { TemplateDetails, Field, Tag, DataType, CalculationDataTypeValue, CalculationType, Team, dataTypeByFieldType } from "@/types"
import api from "@/services/api.ts"
import FieldAndValue from "@/components/Controls/FieldAndValue.vue"
import { CaseColumn, caseColumns } from "@/types/caseColumns"
import { TemplateSchedule } from "@/types/templates/templateSchedule"
import { LabeledValue } from "@/types/scheduleSettings"
const setup = namespace("setup")

@Component({
	components: {
		"data-form": DataForm
	}
})
export default class TemplateScheduling extends Vue {
	@Prop()
	public template!: TemplateDetails

	@Prop({ type: Boolean, default: true })
	public canEdit!: boolean;

	@setup.Getter("getTeams") teams!: Team[];

	get filterOutCaseColumns () {
		var filteredList: string[] = ["created", "closed", "openForDays", "isClosed", "templateId", "caseNumber", "lastUpdated", "deleted", "hiddenFromInsights"]
		var fields = this.template.fields
		var schedulingFields = this.template.templateSchedule?.schedulingFields

		if (fields.some(field => field.field.dataType === "user-role")) {
			filteredList = [...filteredList, "assigneeId", "users", "pendingTeams"]
		}

		if (this.template.hiddenFields != null) {
			var hiddenFields = this.template.hiddenFields
			hiddenFields.map(function (field) {
				switch (field) {
					case "status":
						filteredList = [...filteredList, "statusId"]
						break
					case "severity":
						filteredList = [...filteredList, "severityId"]
						break
					case "affectedAreas":
						filteredList = [...filteredList, "areas"]
						break
				}
			})
			filteredList = [...filteredList, ...this.template.hiddenFields]
		}

		if (this.template.alwaysPromoted) {
			filteredList.push("isCase")
		}

		if (this.template.enableTitleTemplate) {
			filteredList.push("title")
		}

		Vue.set(this.template.templateSchedule!, "schedulingFields", schedulingFields?.filter(field => !filteredList.includes(field)))

		return filteredList
	}

	public async mounted () {
		if (!this.template.templateSchedule) {
			var templateSchedule: TemplateSchedule = {
				schedulingAvailable: false,
				schedulingPermissions: [],
				schedulingFields: [],
				defaultValues: {}
			}
			this.template.templateSchedule = templateSchedule
		} else if (this.template.templateSchedule.defaultValues && Object.keys(this.template.templateSchedule.defaultValues!).length > 0) {
			this.sortScheduleValues()
		}
	}

	public get columns (): any[] {
		if (!this.template) {
			return []
		}

		return [
			{
				key: "schedulingAvailable",
				label: "Available for Scheduling",
				dataType: "checkbox",
				valueColumn: "schedulingAvailable",
				inModal: (item: any) => true,
				inTable: false,
				tooltip: "When enabled this template will be available to use for Scheduling."
			},
			{
				key: "schedulingPermissions",
				hasTemplate: true,
				label: "Schedule Permissions",
				dataType: "list-multiple",
				valueColumn: "schedulingPermissions",
				options: this.teams,
				inTable: false,
				placeholder: "Any"
			},
			{
				key: "schedulingFields",
				label: "Available Fields",
				dataType: "list-multiple",
				valueColumn: "schedulingFields",
				options: this.allFields,
				inModal: (item: any) => true,
				inTable: false,
				placeholder: "All Fields"
			},
			{
				key: "defaultValues",
				label: "Default Fields",
				dynamicComponent: FieldAndValue,
				componentProperties: {
					fields: this.availableFields,
					allowEmptyFieldValue: true
				},
				inModal: (item: any) => true,
				inTable: false
			}]
	}

	/**
	 * List of all the fields added to the template
	 */
	get availableFields () {
		// return all fields if available
		if (this.template.templateSchedule!.schedulingFields!.length === 0) {
			return this.allFields
		}
		return this.allFields.filter(f => this.template.templateSchedule!.schedulingFields!.includes(f.key))
	}

	/**
	 * List of all fields that can be used for Scheduling
	 */
	get allFields (): CaseColumn[] {
		// Map the default and created fields
		return [
			...caseColumns.filter(f => this.filterOutCaseColumns.includes(f.key) === false).map(f => {
				return {
					id: f.key,
					key: f.key,
					title: f.label,
					label: f.label,
					fieldId: f.fieldId,
					dataType: f.dataType,
					dataSourceId: f.dataSourceId,
					dynamicField: true,
					fieldExtensions: f.fieldExtensions,
					fieldDataType: f.dataType === "calculated"
						? DataType.number
						: dataTypeByFieldType[f.dataType!]
							? dataTypeByFieldType[f.dataType!]
							: DataType.string
				}
			}),
			...(this.template.fields ? this.template.fields
				.map(f => {
					return {
						id: f.field.id,
						title: f.field.title,
						label: f.field.title,
						key: f.field.id!,
						fieldId: f.field.id,
						dataType: f.field.dataType!,
						dataSourceId: f.field.dataSourceId,
						dynamicField: true,
						fieldExtensions: f.field.extensions,
						fieldDataType: f.field.dataType === "calculated"
							? DataType.number
							: dataTypeByFieldType[f.field.dataType!]
								? dataTypeByFieldType[f.field.dataType!]
								: DataType.string
					}
				})
				: [])
		]
	}

	sortScheduleValues () {
		if (this.template.templateSchedule) {
			const defaults = this.template.templateSchedule.defaultValues

			if (defaults) {
				this.template.templateSchedule.defaultValues = Object.keys(defaults)
					.sort((a, b) => {
						const fieldA = this.template.fields.find(f => f.field.id === a)
						const fieldB = this.template.fields.find(f => f.field.id === b)

						if (!fieldA && !fieldB) {
							return 0	// a and b not found
						}

						if (!!fieldA && !fieldB) {
							return 1	// b not found
						}

						if (!fieldA && !!fieldB) {
							return -1	// a not found
						}

						return fieldA!.order - fieldB!.order
					})
					.reduce((obj: {[key: string]: LabeledValue}, key: string) => {
						obj[key] = defaults![key]
						return obj
					}, {})
			}
		}
	}
}

